import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Stack,
  Text,
} from "@chakra-ui/react";
import { FC, useEffect, useMemo, useState } from "react";
import { borderRadius, borderStyle } from "../../utils/styles/styles";
import { useNavigate } from "react-router-dom";
import { Option } from "../../types/General/Option";
import { useFilter } from "../../hooks/filter/useFilter";
import { useForm } from "react-hook-form";
import { getCountyById } from "../../services/TabelasAuxiliares/getCountyById";
import useFetchData from "../../hooks/data/useFetchData";
import { getProvince } from "../../services/TabelasAuxiliares/getProvince";
import { dashboardSelectFilterData } from "./data/dashboardSelectFilterData";
import { BsFileBarGraph, BsGeoAlt } from "react-icons/bs";
import { ChevronRightIcon } from "@chakra-ui/icons";
import FieldOccurencesState from "../FieldOccurrences/component/FieldOccurencesState";
import CountDownDashboard from "./component/CountDownDashboard";
import { getFiltersArray } from "../../helpers/getFiltersArray";
import SpinnerComponent from "../../components/atom/Spinner/SpinnerComponent";
import { useCustomModal } from "../../hooks/modal/useCustomModal";
import { ModalConfig } from "../../types/Components/Modal/ModalConfig";
import ModalBulletinGraph from "../Bulletins/component/ModalBulletinGraph";
import { GraphInfo } from "../../types/Graph/GraphInfo";
import { getBulletinIndicator } from "../../services/Bulletins/getBulletinIndicator";
import { getDashboard } from "../../services/Dashboard/getDashboard";
import { GraphBulletinInfo } from "../../types/Graph/GraphBulletinInfo";
import { renderGraphs } from "../Bulletins/bulletinPDF/renderGraphs";
import { useAuth } from "../../provider/AuthProvider";
import { applyRoleBasedFilters } from "../../helpers/Filter/authUserFilter";

const Dashboard: FC = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const user = auth.user;
  const [totalOccurences, setTotalOccurences] = useState<number | null>(null);
  const [totalIndicatorChave, setTotalIndicatorChave] = useState<number | null>(
    null,
  );
  const [totalIndicatorCommun, setTotalIndicatorCommun] = useState<
    number | null
  >(null);
  const [selectedProvince, setSelectedProvince] = useState<Option | null>(null);
  const [provinceOptions, setProvinceOptions] = useState<Option[]>([]);
  const [selectedCounty, setSelectedCounty] = useState<Option | null>(null);
  const [countyOptions, setCountyOptions] = useState<Option[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [summaryHeading, setSummaryHeading] = useState<string>("");
  const [countyLoaded, setCountyLoaded] = useState<boolean>(false);
  const [showData, setShowData] = useState<boolean>(false);
  const [defaultGraphValue, setDefaultGraphValue] = useState<GraphInfo[]>([]);
  const [dataTableGraphs, setDataTableGraphs] = useState<GraphBulletinInfo[]>(
    [],
  );
  const {
    control,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const filterNames = useMemo(() => ["ProvinceId", "CountyId"], []);

  const { filters, setFilter } = useFilter({ setValue, filterNames });
  const { isLoaded: provinceLoaded } = useFetchData(
    getProvince,
    setProvinceOptions,
  );

  useEffect(() => {
    if (user?.role?.level?.id >= 4 && user?.province) {
      const province = {
        id: user.province.id,
        name: user.province.name,
      };
      setSelectedProvince(province);

      if (user.county) {
        const county = {
          id: user.county.id,
          name: user.county.name,
        };
        setSelectedCounty(county);
      }
      setIsSearching(true);
      setShowData(true);
    }
  }, [user]);

  useEffect(() => {
    const fetchData = (filterValues?: any) => {
      const updatedFilters = applyRoleBasedFilters(
        user,
        filterValues,
        setFilter,
      );
      getDashboard(updatedFilters)
        .then((result) => {
          setTotalIndicatorChave(result.indicatorKeyAlertCount);
          setTotalIndicatorCommun(result.indicatorCommonAlertCount);
          setTotalOccurences(result.fieldOccurencesCount);
          setDataTableGraphs(result.indicatorsDataGraphs);
          setIsSearching(false);
        })
        .catch((error) => {
          console.log("API Error:", error);
          setIsSearching(false);
        });

      const newHeading = getSummaryHeading();
      setSummaryHeading(newHeading);
    };
    isSearching && selectedProvince && fetchData(filters);

    isSearching && !selectedProvince && setShowData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching, selectedProvince]);

  useEffect(() => {
    const updateCountyOptions = () => {
      setCountyLoaded(false);
      if (selectedProvince) {
        getCountyById({ ParentId: selectedProvince.id })
          .then((result) => setCountyOptions(result))
          .finally(() => {
            setCountyLoaded(true);
          });
      } else {
        setCountyOptions([]);
        setCountyLoaded(true);
      }
    };
    updateCountyOptions();
  }, [selectedProvince]);

  const filterChildren = dashboardSelectFilterData(
    provinceOptions,
    countyOptions,
    control,
    setFilter,
    setSelectedProvince,
    setSelectedCounty,
    selectedProvince,
    provinceLoaded,
    countyLoaded,
    user,
  );

  const filtersArray = getFiltersArray(filterNames, filters, setFilter);

  const handleCleanFilters = () => {
    filtersArray.forEach((filter) => {
      filter.setValue("");
    });
    setSelectedProvince(
      user?.role?.level?.id >= 4 && user?.province
        ? { id: user.province.id, name: user.province.name }
        : null,
    );
    setSelectedCounty(
      user?.role?.level?.id === 5 && user?.county
        ? { id: user.county.id, name: user.county.name }
        : null,
    );
    setIsSearching(true);
    setShowData(false);
  };

  const getSummaryHeading = () => {
    if (selectedProvince?.name && selectedCounty?.name) {
      return `Resumo de ${selectedProvince.name} / ${selectedCounty.name}`;
    } else if (selectedProvince?.name) {
      return `Resumo de ${selectedProvince.name} / Todos os Municípios`;
    } else {
      return "Resumo de Todas as províncias (Nacional)";
    }
  };

  const handleSelectBulletinGraph = () => {
    openModal("Gerir gráfico(s)", "Guardar");

    const initialGraphs = defaultGraphValue.map((graph) => ({
      indicatorOptions: {
        id: graph.indicator.id,
        name: graph.indicator.name,
      },
      typeOption: {
        id: graph.graphicType.id,
        name: graph.graphicType.name,
      },
    }));

    setValue("graphs", initialGraphs);
  };

  const { isOpen, title, buttonTitle, openModal, closeModal } = useCustomModal(
    "",
    "",
  );

  const modalConfig: ModalConfig = {
    isOpen,
    title,
    buttonTitle,
    openModal,
    closeModal,
  };

  useFetchData(getBulletinIndicator, setDefaultGraphValue);

  useEffect(() => {
    const fetchData = () => {
      getBulletinIndicator()
        .then((result) => {
          setDefaultGraphValue(result);
        })
        .catch((error) => {
          console.log("API Error:", error);
        });
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Stack spacing={8} marginTop="8">
      <Flex direction="column">
        <Heading as="h1" fontSize="4xl">
          Painel de Controlo
        </Heading>
      </Flex>
      <Flex
        bgGradient="linear(130deg, #062952 10%, #1F6B54 100%)"
        padding="8"
        borderRadius={borderRadius}
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex direction="column" gap="2">
          <Heading as="h2" fontSize="3xl" color="white">
            Boletins
          </Heading>
          {user.permissions.includes("GetBulletin") && (
            <>
              <Text fontSize="md" fontStyle="normal" color="white">
                Consulte e descarregue os boletins gerados
              </Text>
              <Box mt="2">
                <Button
                  variant="secondary"
                  bg="white"
                  onClick={() => navigate("/boletins")}
                >
                  Aceder aos Boletins
                </Button>
              </Box>
            </>
          )}
        </Flex>

        <Flex direction="row" gap={4} alignItems="center">
          <CountDownDashboard />
        </Flex>
      </Flex>
      <Box>
        <Box as="hr" borderColor="gray.300" marginBottom="4" />
        <Flex
          direction="column"
          gap="4"
          backgroundColor="gray.200"
          padding="4"
          borderTopRadius={borderRadius}
        >
          <Flex alignItems="center" justifyContent="space-between">
            <Heading as="h3" fontSize="2xl">
              Gerar Resumo
            </Heading>

            {user.permissions.includes("UpdateBulletinIndicators") && (
              <Button variant="secondary" onClick={handleSelectBulletinGraph}>
                Gerir gráfico(s)
              </Button>
            )}
          </Flex>
          <Flex gap="4" alignItems="flex-end">
            {filterChildren}
            <Button
              variant="primary"
              onClick={() => {
                setIsSearching(true);
                selectedProvince && setShowData(true);
              }}
            >
              Gerar resumo
            </Button>
            <Button variant="secondary" onClick={handleCleanFilters}>
              Limpar
            </Button>
          </Flex>
        </Flex>
        {showData && (
          <Flex
            direction="column"
            gap="4"
            backgroundColor="gray.50"
            padding="6"
            borderBottomRadius={borderRadius}
          >
            <Heading as="h3" fontSize="2xl">
              {summaryHeading}
            </Heading>
            <Flex gap="4">
              <Box width="50%">
                <Flex
                  gap="8"
                  alignItems="center"
                  backgroundColor="white"
                  padding="6"
                  borderTopRadius={borderRadius}
                  border={borderStyle}
                >
                  <Box>
                    <Box
                      bgGradient="linear(130deg, #062952 10%, #1F6B54 100%)"
                      padding="3"
                      paddingBottom="2"
                      borderRadius={borderRadius}
                    >
                      <Icon
                        aria-label="Aceder aos Indicadores"
                        as={BsFileBarGraph}
                        boxSize={6}
                        color="white"
                      />
                    </Box>
                  </Box>
                  <Flex direction="column" gap="2" width="100%">
                    <Text fontSize="sm" fontStyle="normal">
                      Indicadores em alerta
                    </Text>
                    <Flex gap="2" alignItems="center">
                      <Heading fontSize="2xl" as="h3">
                        {totalIndicatorChave !== null ? (
                          totalIndicatorChave
                        ) : (
                          <SpinnerComponent />
                        )}
                      </Heading>
                      <Text fontSize="md" fontStyle="normal">
                        indicadores-chave
                      </Text>
                    </Flex>
                    <Box
                      as="hr"
                      borderColor="gray.300"
                      marginBottom="0"
                      width="100%"
                    />

                    <Flex gap="2" alignItems="center">
                      <Heading fontSize="2xl" as="h3">
                        {totalIndicatorCommun !== null ? (
                          totalIndicatorCommun
                        ) : (
                          <SpinnerComponent />
                        )}
                      </Heading>
                      <Text fontSize="md" fontStyle="normal">
                        indicadores-comum
                      </Text>
                    </Flex>
                  </Flex>
                </Flex>
                <Box
                  padding="6"
                  borderBottomRadius={borderRadius}
                  backgroundColor="gray.300"
                >
                  <Button
                    variant="tertiary"
                    onClick={() => {
                      const filters = {
                        indicatorLevel: selectedCounty?.id
                          ? { id: 3, name: "Municipal" }
                          : { id: 4, name: "Provincial" },
                        province: selectedProvince && {
                          id: selectedProvince.id,
                          name: selectedProvince.name,
                        },
                        county: selectedCounty && {
                          id: selectedCounty.id,
                          name: selectedCounty.name,
                        },
                      };

                      navigate("/indicadores", { state: { filters } });
                    }}
                    rightIcon={<ChevronRightIcon boxSize={5} />}
                  >
                    Aceder aos Indicadores
                  </Button>
                </Box>
              </Box>
              <Flex width="50%" direction="column">
                <Flex
                  gap="8"
                  alignItems="center"
                  backgroundColor="white"
                  padding="6"
                  borderTopRadius={borderRadius}
                  border={borderStyle}
                  alignSelf="stretch"
                  flexGrow={1}
                >
                  <Box>
                    <Box
                      bgGradient="linear(130deg, #062952 10%, #1F6B54 100%)"
                      padding="3"
                      paddingBottom="2"
                      borderRadius={borderRadius}
                    >
                      <Icon
                        aria-label="Aceder aos Indicadores"
                        as={BsGeoAlt}
                        boxSize={6}
                        color="white"
                      />
                    </Box>
                  </Box>
                  <Flex direction="column" gap="2" width="100%">
                    <Text fontSize="sm" fontStyle="normal">
                      Ocorrências no Terreno
                    </Text>
                    <Flex gap="2" alignItems="center">
                      <Heading fontSize="2xl" as="h3">
                        {totalOccurences !== null ? (
                          totalOccurences
                        ) : (
                          <SpinnerComponent />
                        )}
                      </Heading>
                      <FieldOccurencesState
                        stateValue={true}
                        additionalStyle={{ display: "inline-block" }}
                      />
                    </Flex>
                  </Flex>
                </Flex>
                <Flex
                  padding="6"
                  borderBottomRadius={borderRadius}
                  backgroundColor="gray.300"
                >
                  <Button
                    variant="tertiary"
                    onClick={() => {
                      const filters = {
                        province: selectedProvince && {
                          id: selectedProvince.id,
                          name: selectedProvince.name,
                        },
                        county: selectedCounty && {
                          id: selectedCounty.id,
                          name: selectedCounty.name,
                        },
                      };

                      navigate("/ocorrencias-no-terreno", {
                        state: { filters },
                      });
                    }}
                    rightIcon={<ChevronRightIcon boxSize={5} />}
                  >
                    Aceder às Ocorrências no Terreno
                  </Button>
                </Flex>
              </Flex>
            </Flex>
            {dataTableGraphs && (
              <Flex direction="column" gap="4">
                {renderGraphs(dataTableGraphs, "xl", "bold")}
              </Flex>
            )}
          </Flex>
        )}
      </Box>
      <ModalBulletinGraph
        control={control}
        errors={errors}
        handleSubmit={handleSubmit}
        modalConfig={modalConfig}
        watch={watch}
      />
    </Stack>
  );
};

export default Dashboard;
